<!-- This example requires Tailwind CSS v2.0+ -->
<template>
  <TransitionRoot
    as="template"
    :show="showDispositionModal"
  >
    <Dialog
      as="div"
      :static="true"
      class="fixed inset-0 z-10 overflow-y-auto"
    >
      <div
        class="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0"
      >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay
            class="fixed inset-0 transition-opacity bg-gray-500/75"
          />
        </TransitionChild>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span
          class="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >&#8203;</span>
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div
            class="relative inline-block px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-sm sm:w-full sm:p-6"
          >
            <div>
              <div
                class="flex items-center justify-center w-12 h-12 mx-auto bg-gray-100 rounded-full"
              >
                <ld-icon
                  icon="TagIcon"
                  class="w-6 h-6 text-gray-600"
                  aria-hidden="true"
                />
              </div>
              <div class="mt-3 text-center sm:mt-5">
                <DialogTitle
                  as="h3"
                  class="text-lg font-medium leading-6 text-gray-900"
                >
                  Please Select A Disposition For This Call
                </DialogTitle>
              </div>
            </div>
            <div
              v-if="hasCallbackOption"
              class="mt-5 sm:mt-6"
            >
              <button
                v-if="showCallback === false"
                type="button"
                class="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
                @click="showCallback = true"
              >
                <ld-icon
                  class="w-5 h-5 mr-2 -ml-1 text-gray-400"
                  icon="ClockIcon"
                  aria-hidden="true"
                />
                <span>Schedule Callback</span>
              </button>
              <div
                v-if="showCallback === true"
                class="mt-2"
              >
                <label
                  for="CallBackAt"
                  class="block text-sm font-medium text-gray-700"
                >Schedule Callback for</label>
                <Datepicker
                  v-model="scheduledFor"
                  name="CallBackAt"
                  auto-apply
                  multi-calendars
                />
                <label
                  for="comment"
                  class="block text-sm font-medium text-gray-700"
                >comment</label>
                <div class="mt-1">
                  <textarea
                    id="comment"
                    v-model="note"
                    rows="4"
                    name="comment"
                    class="block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
                  />
                </div>
              </div>
            </div>
            <div class="mt-5 sm:mt-6">
              <button
                v-if="selectedDisposition"
                :disabled="submitting"
                type="button"
                class="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-teal-600 border border-transparent rounded-md shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-real-500 sm:text-sm disabled:opacity-50"
                @click="back"
              >
                Back
              </button>
              <button
                v-for="disposition in dispositions"
                :key="disposition.id"
                :disabled="submitting"
                type="button"
                class="inline-flex justify-center w-full px-4 py-2 mt-2 text-base font-medium text-white bg-teal-600 border border-transparent rounded-md shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-real-500 sm:text-sm disabled:opacity-50"
                @click="selectDisposition(disposition)"
              >
                <ld-icon
                  v-if="submitting"
                  icon="RefreshIcon"
                  class="w-4 h-4 mr-2 animate-spin"
                />
                {{ disposition.attributes.name }}
              </button>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { ref, defineComponent, Ref, computed, watch, onMounted } from 'vue'
import {
  Dialog,
  DialogOverlay,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'
import useLetsDialSocket from '/@/services/letsDialSocket/useLetsDialSocket'
import { useCallStore } from '/@/store/callStore'
import useLetsDialAPI from '/@/services/letsDialAPI/useLetsDialAPI'
import { OrganizationDisposition } from '@letsdial/api/src/lib/types/OrganizationDisposition'
import { useRecordStore } from '/@/store/recordStore'
import { useLetsDialExtensions } from '/@/services/useLetsDialExtensions'
import LDInput from '/@/components/ui/form/LDInput.vue'
import { Campaign } from '@letsdial/api/src'
import Datepicker from '@vuepic/vue-datepicker'
import { useNotificationStore } from '/@/store/notificationStore'
import { useAuthStore } from '/@/store/authStore'

const props = withDefaults(defineProps<{ open: boolean }>(), {
  open: () => false,
})

const selectedDisposition: Ref<OrganizationDisposition | undefined> =
  ref(undefined)
const auth = useAuthStore()
const callStore = useCallStore()
const recordStore = useRecordStore()
const notificationStore = useNotificationStore()
const api = useLetsDialAPI()
const { socket } = useLetsDialSocket()
const { disableWrapOnCallEnd } = useLetsDialExtensions()

const submitting = ref(false)

const showCallback = ref(false)
const scheduledFor = ref('')
const note = ref('')

const showDispositionModal = computed(() => {
  return (
    callStore.status === 'TERMINATED' &&
    ['customCalls', 'flowControllCalls'].includes(
      callStore.call?.attributes.callType || '',
    ) &&
    callStore.hasDispositions
  )
})

watch(
  () => callStore.status,
  (val) => {
    if (val === 'TERMINATED' && callStore.hasDispositions) {
      setWrapupStatus()
    }
  },
)

const dispositions = computed(() => {
  if (!selectedDisposition.value?.id) {
    return (
      callStore.organizationDispositions?.filter(
        (dispo) => dispo.relationships.parent.data === null,
      ) ?? []
    )
  } else {
    return (
      callStore.organizationDispositions?.filter(
        (dispo) =>
          dispo.relationships.parent.data?.id === selectedDisposition.value?.id,
      ) ?? []
    )
  }
})

const hasCallbackOption = computed(() => {
  return auth.selectedOrganization?.attributes?.additionalConfig?.allowCallBack ?? false
})

const selectDisposition = async (
  organizationDisposition: OrganizationDisposition,
) => {
  if (submitting.value) return

  const children =
    callStore.organizationDispositions?.filter(
      (dispo) =>
        dispo.relationships.parent.data?.id === organizationDisposition?.id,
    ) || []

  if (organizationDisposition.id && children.length === 0) {
    const callId = callStore.call?.id || recordStore.pop?.callID

    if (!callId) {
      notificationStore.setNotification(
        'Missing Call ID',
        'Could not find an active call to disposition. Please refresh the page if this persists.',
        'Error',
      )
      return
    }

    submitting.value = true
    try {
      if (callStore.call) {
        callStore.call.relationships = {
          organizationDisposition: {
            data: {
              type: 'organizationDispositions',
              id: organizationDisposition.id,
            },
          },
        }
      }

      if (scheduledFor.value && hasCallbackOption.value) {
        const number = callStore.call?.attributes?.number
        const organizationId = auth.selectedOrganization?.id
        if (auth.user?.id && organizationId) {
          await api.callbacks.post({
            type: 'callbacks',
            attributes: {
              scheduledFor: scheduledFor.value,
              note: note.value,
              phoneNumber: number,
            },
            relationships: {
              user: { data: { type: 'users', id: auth.user.id } },
              call: {
                data: {
                  type: 'calls',
                  id: callId,
                },
              },
              organization: {
                data: {
                  type: 'organizations',
                  id: organizationId,
                },
              },
            },
          })
          scheduledFor.value = ''
          showCallback.value = false
        }
      }

      await api.calls.patch(callId, {
        id: callId,
        type: 'calls',
        attributes: {},
        relationships: {
          organizationDisposition: {
            data: {
              type: 'organizationDispositions',
              id: organizationDisposition.id,
            },
          },
        },
      })

      callStore.$reset()
      auth.setAvailabilityStatus('IDLE')
      selectedDisposition.value = undefined
    } catch (e) {
      console.error('Failed to save disposition', e)
      notificationStore.setNotification(
        'Submission Failed',
        'An error occurred while saving the disposition. Please try again.',
        'Error',
      )
    } finally {
      submitting.value = false
    }
  } else {
    selectedDisposition.value = organizationDisposition
  }
}

const setWrapupStatus = () => {
  console.log('settings Status')
  auth.setAvailabilityStatus('IN_WRAPUP')
}

const back = () => {
  if (!selectedDisposition.value?.relationships?.parent.data?.id) {
    selectedDisposition.value = undefined
  }
  selectedDisposition.value = callStore.organizationDispositions?.find(
    (dispo) =>
      dispo.id === selectedDisposition.value?.relationships.parent.data?.id,
  )
}
</script>
